/**
 * FeaturedEntityCard - Educational featured entity display for no-results scenarios
 * Shows when users have no search results, helping them discover the platform
 * 
 * Features:
 * - Context-aware messaging (no location, no results, filtered out)
 * - Interactive discovery dots explaining key features
 * - Multiple CTAs for engagement (View, Join Channel, See Sightings)
 * - Map integration button
 * - Responsive design
 * - Collapsible with chevron indicator
 * 
 * @idempotent - Pure display component with external state
 * @atomic - Single purpose: featured entity educational card (<300 lines)
 */
import { memo, useState } from 'react'
import { 
  StarIcon, 
  MapPinIcon, 
  ChatBubbleLeftRightIcon,
  EyeIcon
} from '@heroicons/react/24/solid'
import { 
  ChevronUpIcon,
  ChevronDownIcon,
  XMarkIcon
} from '@heroicons/react/24/outline'
import { useTranslation } from 'react-i18next'
import { hideImageOnError } from '../../hooks/useImageErrorHandler'
import { trackEvent } from '../../utils/analytics'
import { createLogger } from '../../utils/logger'

const log = createLogger('FeaturedEntityCard')

const FeaturedEntityCard = memo(function FeaturedEntityCard({
  entity,
  onViewDetails,
  onShowOnMap,
  onJoinChannel,
  onDismiss = null,
  reason = 'no-results',
  className = ''
}) {
  const { t } = useTranslation()
  const [isExpanded, setIsExpanded] = useState(false)

  if (!entity) return null

  const handleClick = (action, ctaName) => {
    log.debug('User clicked CTA', { action, ctaName, entityId: entity.id, entityName: entity.name })
    
    trackEvent('Featured Entity', action, entity.id, { reason, cta: ctaName })
    
    switch (action) {
      case 'view_details':
        onViewDetails?.(entity)
        break
      case 'show_on_map':
        onShowOnMap?.(entity)
        break
      case 'join_channel':
        onJoinChannel?.(entity)
        break
      default:
        log.warn('Unknown action', { action })
        break
    }
  }

  const handleToggle = () => {
    const newState = !isExpanded
    setIsExpanded(newState)
    
    log.debug(`Card ${newState ? 'expanded' : 'collapsed'}`, { 
      entityId: entity.id,
      entityName: entity.name,
      reason 
    })
    
    // Track expansion/collapse
    trackEvent('Featured Entity', newState ? 'expand' : 'collapse', entity.id, { 
      reason,
      action: 'chevron_click'
    })
  }

  const getContextMessage = () => {
    switch (reason) {
      case 'no-location':
        return t('map.featuredEntity.noLocation')
      case 'filtered-out':
        return t('map.featuredEntity.filteredOut')
      default:
        return t('map.featuredEntity.noResults')
    }
  }

  return (
    <div className={`bg-white/95 backdrop-blur-sm border border-primary/30 rounded-xl shadow-md overflow-hidden ${className}`}>
      {/* Header row — always visible */}
      <div className="flex items-center gap-3 px-4 py-3">
        <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
          <StarIcon className="h-4 w-4 text-primary" />
        </div>

        {/* Title + context message */}
        <div
          role="button"
          tabIndex={0}
          onClick={handleToggle}
          onTouchEnd={(e) => { e.preventDefault(); handleToggle() }}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleToggle() } }}
          className="flex-1 min-w-0 cursor-pointer select-none"
          style={{ WebkitTapHighlightColor: 'transparent' }}
        >
          <p className="text-sm font-semibold text-gray-800 truncate pointer-events-none">{entity.name}</p>
          <p className="text-xs text-gray-500 truncate pointer-events-none">{getContextMessage()}</p>
        </div>

        {/* Right controls: chevron + dismiss */}
        <div className="flex items-center gap-1 flex-shrink-0 ml-1">
          <button
            type="button"
            onClick={handleToggle}
            onTouchEnd={(e) => { e.preventDefault(); handleToggle() }}
            className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors focus:outline-none"
            aria-label={isExpanded ? t('common.collapse') : t('common.expand')}
            aria-expanded={isExpanded}
          >
            {isExpanded
              ? <ChevronUpIcon className="h-4 w-4 pointer-events-none" />
              : <ChevronDownIcon className="h-4 w-4 pointer-events-none" />}
          </button>

          {onDismiss && (
            <button
              type="button"
              onClick={onDismiss}
              onTouchEnd={(e) => { e.preventDefault(); onDismiss() }}
              className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors focus:outline-none"
              aria-label={t('common.close')}
            >
              <XMarkIcon className="h-4 w-4 pointer-events-none" />
            </button>
          )}
        </div>
      </div>

      {/* Expandable body */}
      {isExpanded && (
        <div className="px-4 pb-4 space-y-3 border-t border-gray-100 pt-3">
          {/* Entity image */}
          {((entity.imageUrl && entity.imageUrl.trim()) || (entity.image_url && entity.image_url.trim())) && (
            <img
              src={entity.imageUrl || entity.image_url}
              alt={entity.name}
              className="w-full h-32 object-cover rounded-lg"
              onError={hideImageOnError}
            />
          )}

          {/* CTA buttons */}
          <div className="flex flex-col gap-2">
            <button
              type="button"
              onClick={() => handleClick('view_details', 'view_details')}
              onTouchEnd={(e) => { e.preventDefault(); handleClick('view_details', 'view_details') }}
              className="w-full inline-flex items-center justify-center px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-600 transition-colors text-sm font-semibold"
            >
              <EyeIcon className="h-4 w-4 mr-2" />
              {t('map.viewDetails')}
            </button>

            {onShowOnMap && (
              <button
                type="button"
                onClick={() => handleClick('show_on_map', 'show_on_map')}
                onTouchEnd={(e) => { e.preventDefault(); handleClick('show_on_map', 'show_on_map') }}
                className="w-full inline-flex items-center justify-center px-4 py-2 bg-white text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-sm font-semibold"
              >
                <MapPinIcon className="h-4 w-4 mr-2" />
                {t('map.featuredEntity.showOnMap')}
              </button>
            )}

            {onJoinChannel && (
              <button
                type="button"
                onClick={() => handleClick('join_channel', 'join_channel')}
                onTouchEnd={(e) => { e.preventDefault(); handleClick('join_channel', 'join_channel') }}
                className="w-full inline-flex items-center justify-center px-4 py-2 bg-white text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-sm font-semibold"
              >
                <ChatBubbleLeftRightIcon className="h-4 w-4 mr-2" />
                {t('channel.joinDiscussion')}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  )
})

export default FeaturedEntityCard
